import AddIcon from '@mui/icons-material/Add';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SearchIcon from '@mui/icons-material/Search';
import { Typography } from '@mui/material';
import Button from '@shared/components/Buttons/Button';
import Colors from '@shared/styles/Colors';
import { easeOut, motion, spring } from 'framer-motion';
import styled from 'styled-components';

const EmptyStateContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  background: linear-gradient(135deg, #e3f0fb 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed rgba(120, 187, 241, 0.4);
  margin: 32px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 48px 20px;
  }
`;

const IconWrapper = styled(motion.div)`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #0b74da 0%, #2952a3 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px rgba(11, 116, 218, 0.3);

  svg {
    font-size: 60px;
    color: ${Colors.white};
  }
`;

const Title = styled(Typography)`
  font-size: 28px;
  font-weight: 700;
  color: ${Colors.black};
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Description = styled(Typography)`
  font-size: 16px;
  color: ${Colors.greyDarker};
  max-width: 600px;
  margin-bottom: 40px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 32px;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 600px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const CTACard = styled(motion.div)`
  background: ${Colors.white};
  border-radius: 12px;
  padding: 24px;
  width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid ${Colors.grey};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #0b74da;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const CTATopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
`;

const CTAIconWrapper = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  background: ${(props) => props.color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 32px;
    color: white;
  }
`;

const CTATitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: ${Colors.black};
  margin-bottom: 8px;
`;

const CTADescription = styled(Typography)`
  font-size: 14px;
  color: ${Colors.greyDark};
  margin-bottom: 16px;
  line-height: 1.5;
`;

interface CompanyProfileEmptyStateProps {
  companyName: string;
  onLeaveReview: () => void;
  onCreateIntelligenceRequest: () => void;
  isTrialExpired?: boolean;
  hideIntelligenceRequest?: boolean;
}

function CompanyProfileEmptyState({
  companyName,
  onLeaveReview,
  onCreateIntelligenceRequest,
  isTrialExpired = false,
  hideIntelligenceRequest = false,
}: CompanyProfileEmptyStateProps) {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeOut,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: spring,
        stiffness: 260,
        damping: 20,
      },
    },
  };

  return (
    <EmptyStateContainer variants={containerVariants} initial="hidden" animate="visible">
      <IconWrapper variants={iconVariants}>
        <SearchIcon />
      </IconWrapper>

      <Title variant="h4">No Deal Reviews Yet for {companyName}</Title>

      <Description>
        Be the first to share your experience selling to {companyName}. Your insights can help fellow sales
        professionals close more deals and navigate the sales process with confidence.
      </Description>

      <CTAContainer>
        <CTACard
          variants={itemVariants}
          onClick={onLeaveReview}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="empty-state-leave-review"
        >
          <CTATopSection>
            <CTAIconWrapper color="linear-gradient(135deg, #0b74da 0%, #2952a3 100%)">
              <HandshakeIcon />
            </CTAIconWrapper>
            <CTATitle>Add the First Deal Retro</CTATitle>
            <CTADescription>Share your deal retro and earn credits while helping others succeed</CTADescription>
          </CTATopSection>
          <Button
            size="medium"
            variantType="success"
            startIcon={<HandshakeIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onLeaveReview();
            }}
            fullWidth
          >
            Add Deal Review
          </Button>
        </CTACard>

        {!isTrialExpired && !hideIntelligenceRequest && (
          <CTACard
            variants={itemVariants}
            onClick={onCreateIntelligenceRequest}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="empty-state-create-ir"
          >
            <CTATopSection>
              <CTAIconWrapper color="linear-gradient(135deg, #78bbf1 0%, #0b74da 100%)">
                <AddIcon />
              </CTAIconWrapper>
              <CTATitle>Create Intelligence Request</CTATitle>
              <CTADescription>
                Offer payment to incentivize others to share their insights about {companyName}
              </CTADescription>
            </CTATopSection>
            <Button
              size="medium"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onCreateIntelligenceRequest();
              }}
              fullWidth
            >
              Create Intelligence Request
            </Button>
          </CTACard>
        )}
      </CTAContainer>
    </EmptyStateContainer>
  );
}

export default CompanyProfileEmptyState;
